import {
  skipToken,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import {
  type CreateTask,
  type ReplaceTaskDelegation,
  type UpdateTask
} from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

interface GetTasksQueryParams {
  ownerId?: string[]
  taskIds?: number[]
}

export const useGetTasksQuery = (params: GetTasksQueryParams) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['tasks', params],
    queryFn:
      (params.ownerId != null && params.ownerId.length > 0) ||
      (params.taskIds != null && params.taskIds.length > 0)
        ? async () => {
            const { data, error } = await authFetch().GET('/tasks', {
              params: {
                query: {
                  ownerId: params.ownerId,
                  taskIds: params.taskIds
                }
              }
            })
            if (error) {
              throwOnError(error)
            }
            return data
          }
        : skipToken
  })
}
export const useGetTaskByIdQuery = (taskId?: number) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['tasks', taskId],
    staleTime: 60_000,
    queryFn:
      taskId != null && taskId > 0
        ? async () => {
            const { data, error } = await authFetch().GET('/tasks/{taskId}', {
              params: {
                path: {
                  taskId
                }
              }
            })
            if (error) {
              throwOnError(error)
            }
            return data
          }
        : skipToken
  })
}

export const useGetTaskSampleSummaryQuery = (taskId?: number) => {
  const { authFetchV3 } = useAuth()
  return useQuery({
    queryKey: ['tasks', taskId, 'sample-summary'],
    queryFn:
      taskId != null && taskId > 0
        ? async () => {
            const { data, error } = await authFetchV3().GET(
              '/tasks/{taskId}/sample-summary',
              { params: { path: { taskId } } }
            )
            if (error) throwOnError(error)
            return data
          }
        : skipToken
  })
}

export const useGetTaskDelegationQuery = (taskId?: number, enabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['taskDelegation', taskId],
    queryFn:
      taskId != null && taskId > 0
        ? async () => {
            const { data, error, response } = await authFetch().GET(
              '/tasks/{taskId}/delegation',
              { params: { path: { taskId } } }
            )
            if (error) throwOnError(error)
            const etag = response.headers.get('ETag')
            if (etag == null) throw new Error('Missing delegation ETag')
            return {
              delegation: data,
              etag
            }
          }
        : skipToken,
    enabled
  })
}

export const useGetTaskDelegationsQuery = (
  taskIds: number[],
  enabled = true
) => {
  const { authFetch } = useAuth()
  const queries = useQueries({
    queries: taskIds.map((taskId) => ({
      queryKey: ['taskDelegation', taskId],
      enabled,
      queryFn: async () => {
        const { data, error, response } = await authFetch().GET(
          '/tasks/{taskId}/delegation',
          { params: { path: { taskId } } }
        )
        if (error) throwOnError(error)
        const etag = response.headers.get('ETag')
        if (etag == null) throw new Error('Missing delegation ETag')
        return {
          delegation: data,
          etag
        }
      }
    }))
  })

  return {
    data: queries
      .map((query, index) =>
        query.data == null
          ? undefined
          : ([taskIds[index], query.data.delegation] as const)
      )
      .filter((entry): entry is NonNullable<typeof entry> => entry != null),
    isPending: queries.some((query) => query.isPending),
    isError: queries.some((query) => query.isError),
    error: queries.find((query) => query.error != null)?.error
  }
}

interface ReplaceTaskDelegationMutationVariables {
  taskId: number
  etag: string
  body: ReplaceTaskDelegation
}

export const useReplaceTaskDelegationMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      taskId,
      etag,
      body
    }: ReplaceTaskDelegationMutationVariables) => {
      const { data, error, response } = await authFetch().PUT(
        '/tasks/{taskId}/delegation',
        {
          params: {
            path: { taskId },
            header: { 'If-Match': etag }
          },
          body
        }
      )
      if (error) throwOnError(error)
      const nextEtag = response.headers.get('ETag')
      if (nextEtag == null) throw new Error('Missing delegation ETag')
      return {
        delegation: data,
        etag: nextEtag
      }
    },
    onSuccess: async (_, { taskId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['taskDelegation', taskId] }),
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['plans'] }),
        queryClient.invalidateQueries({ queryKey: ['planSummaries'] }),
        queryClient.invalidateQueries({ queryKey: ['sample-taking-report'] }),
        queryClient.invalidateQueries({ queryKey: ['tasksAudit', taskId] })
      ])
    }
  })
}

export const useCreateTaskMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (task: CreateTask) => {
      const { data, error } = await authFetch().POST('/tasks', {
        body: task
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['tasks']
      })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['plans'] }),
        queryClient.invalidateQueries({ queryKey: ['planSummaries'] }),
        queryClient.invalidateQueries({ queryKey: ['sample-taking-report'] })
      ])
    }
  })
}

interface UpdateTaskMutationVariables {
  taskId: number
  etag: string
  body: UpdateTask
}

export const useUpdateTaskMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ taskId, etag, body }: UpdateTaskMutationVariables) => {
      const { data, error } = await authFetch().PUT('/tasks/{taskId}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: {
            taskId
          }
        },
        body
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['sample-taking-report'] })
  })
}

interface DeleteTaskMutationVariables {
  taskId: number
  etag: string
}

export const useDeleteTaskMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ taskId, etag }: DeleteTaskMutationVariables) => {
      const { data, error } = await authFetch().DELETE('/tasks/{taskId}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: {
            taskId
          }
        }
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['sample-taking-report'] })
  })
}

export const useGetTaskAuditByIdQuery = (taskId?: number) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['tasksAudit', taskId],
    queryFn: async () => {
      if (taskId != null && taskId > 0) {
        const { data, error } = await authFetch().GET(
          '/tasks/{taskId}/delegation-audit',
          {
            params: {
              path: {
                taskId
              }
            }
          }
        )
        if (error) {
          throw new Error('Failed to fetch task audit by ID')
        }
        return data
      }
      return skipToken
    }
  })
}
